import {AziendaRecord} from "@commons/mapping/AziendaRecord";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";

import {CachePresenze} from "@commons/mapping/CachePresenze";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {GiustificativoAttestatoINPSRecord} from "@commons/mapping/GiustificativoAttestatoINPSRecord";
import {CalendarioChiusureRecord} from "@commons/mapping/CalendarioChiusureRecord";
import {SituazioneIC} from "@commons/services/presenze_calcolo/SituazioneIC";
import {DateTime} from "luxon";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import {TurnoDistribuzioneRecord} from "@commons/mapping/TurnoDistribuzioneRecord";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {StatoWeekPLUSRecord} from "@commons/mapping/StatoWeekPLUSRecord";
import {FPNetDate, IndiceMese, Periodo} from "@commons/types";
import {cloneDeep} from "lodash";
import {LavoratoreProprietaOrarioRecord} from "@commons/mapping/LavoratoreProprietaOrarioRecord";
import {LavoratoreStatusRecord} from "@commons/mapping/LavoratoreStatusRecord";
import {PresenzePianificazioneRecord} from "@commons/mapping/PresenzePianificazioneRecord";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import {LavoratorePresenzeProprietaULRecord} from "@commons/mapping/LavoratorePresenzeProprietaULRecord";
import {FlexRecord} from "@commons/mapping/FlexRecord";
import {QualificaEccRecord} from "@commons/mapping/QualificaEccRecord";
import {QuantificaEccRecord} from "@commons/mapping/QuantificaEccRecord";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {Timbrature} from "@commons/services/presenze_calcolo/sub_calcolo/Timbrature";
import {Calcolo} from "@commons/services/presenze_calcolo/Calcolo";
import {GiustificazioneDiMassaTask} from "@commons/services/presenze_calcolo/sub_calcolo/GiustificazioneDiMassaTask";
import {ConsolidatedData, GestionePlusData} from "@commons/services/presenze_calcolo/GestionePlusData";
import {Accumuli} from "@commons/services/presenze_calcolo/sub_calcolo/Accumuli";
import {AziendaMin} from "@commons/mapping/AziendaNavRecord";
import {CalcoloPresenzeTask} from "@commons/services/presenze_calcolo/sub_calcolo/CalcoloPresenzeTask";
import {sort} from "json-keys-sort";

export const solved = (_p0?: any, _p1?: any, _lav?: LavoratorePresenzeRecord) => new Promise<void>((resolve, _reject) => {
    resolve();
})
type Solved = typeof solved;

export const checkAllPromise = (requests: Promise<void>[], resolve: (value: (void | PromiseLike<void>)) => void, log?: string) => {
    if (requests.length > 0) {
        Promise.all(requests).then(() => {
            if (log) {
                console.log(log, ' FINE');
            }
            resolve();
        });
    } else {
        if (log) {
            console.log(log, ', nessuna info da caricare - FINE');
        }
        resolve();
    }
}

export interface GestionePlusOptions {
    isPaginaPlus?: boolean,
    isPaginaWF?: boolean,
    isPaginaPianificazione?: boolean,
    caricaRichiesteWF?: boolean,
    caricaIC?: boolean,
    RichiesteWF?: boolean,
    backupDays?: boolean,
}

export class GestionePlus implements GestionePlusData {
    static readonly maxCausaliOre: number = 5;
    static readonly maxCausaliQI: number = 5;
    idAziendaMaster: number | null = null;
    idAzienda: number | null = null;
    idAziendaTabelle: number | null = null;
    /**
     * Il calendario chiusure viene valorizzato diversamente a seconda che si tratti dell'uso nella pagina di gestione delle presenze (std/plus e workflow employee)
     * oppure nella pagina di monitor workflow o dashboard workflow.
     * Nel primo caso il calendario viene fissato in base alle proprietà di ciascun lavoratore (attraverso le proprietà legate all'unità locale del lavoratore stesso).
     * Nel secondo caso il calendario è quello dell'anno corrispondente con tipologia "2" (workflow) e flag default!
     */
    calendarioChiusure: CalendarioChiusureRecord | null = null;
    elencoUnitaLocali: AziendaRecord[] = [];        // Tutte le unità locali dell'azienda
    elencoLavoratori: LavoratorePresenzeRecord[] | null = []; // Tutti i lavoratori interessati alla gestione delle presenze nel periodo indicato
    elencoLavoratoriInAnomalia: number[] | null = []; // IdDip dei lavoratori in anomalia
    lavoratore: LavoratorePresenzeRecord | null = null; // Lavoratore corrente
    elencoStatus: LavoratoreStatusRecord[] = []; // Gli status del lavoratore corrente
    ic: SituazioneIC | null = null; // SituazioneIC del lavoratore corrente
    preConsolidaLavoratore: Solved = solved;
    postConsolidaLavoratore: Solved = solved;
    modified: boolean = false;
    editEffettive: boolean = false;
    isBusy: boolean = true;
    soloMeseRif: boolean = true; // specifica se nei totali mensili vanno sommati anche i giorni delle settimane a cavallo (false) oppure no (true)
    readonly isPaginaPlus: boolean; // in AngularJS era paginaPlus
    readonly isPaginaWF: boolean; // in AngularJS era paginaWorkflow
    readonly isPaginaPianificazione: boolean;
    readonly caricaRichiesteWF: boolean;
    readonly backupDays: boolean;
    readonly caricaIC: boolean; // Abilita il caricamento dei saldi ferie (esempio in calcolo massivo può essere false)
    elencoTurni: TurnoRecord[] = [];
    days: PresenzeGiornataPlusRecord[] | null = [];
    weeks: StatoWeekPLUSRecord[] | null = [];
    currentWeek: StatoWeekPLUSRecord | null = null;
    totaliMese: StatoWeekPLUSRecord | null = null;
    causaliWF: PresenzeCausaleWFRecord[] = []; // da backend non elaborate
    causaliWFElaborate: string[] = []; // elaborate nel calcolo della giornata corrente
    giustificativiAttestati: GiustificativoAttestatoINPSRecord[] = [];
    giustificativiAttestatiElaborati: string[] = [];
    vociPianificazione: PresenzePianificazioneRecord[] = [];
    proprietaOrario: LavoratoreProprietaOrarioRecord | null = null;
    proprietaGestione: LavoratorePresenzeProprietaULRecord | null = null;
    tabFlex: FlexRecord | null = null;
    tabQualificaEcc: QualificaEccRecord | null = null;
    tabQuantificaEcc: QuantificaEccRecord | null = null;
    distribuzioneTurno: TurnoDistribuzioneRecord | null = null;
    turnoAConsuntivo: TurnoRecord | null = null;
    giustificazioneDiMassa: GiustificazioneDiMassaTask | null = null;
    anno: number = 0;
    mese: IndiceMese = 1;
    private cache: CachePresenze = {} as CachePresenze;

    constructor({
                    isPaginaPlus = false,
                    isPaginaWF = false,
                    isPaginaPianificazione = false,
                    caricaRichiesteWF = false,
                    backupDays = true,
                    caricaIC = true,
                }: GestionePlusOptions) {
        this.isPaginaPlus = isPaginaPlus;
        this.isPaginaWF = isPaginaWF;
        this.isPaginaPianificazione = isPaginaPianificazione;
        this.caricaRichiesteWF = caricaRichiesteWF;
        this.backupDays = backupDays;
        this.caricaIC = caricaIC;
    }

    static getCausaliWFGiorno = (causaliWF: PresenzeCausaleWFRecord[], day: PresenzeGiornataPlusRecord) => {
        const newCausali: PresenzeCausaleWFRecord[] = [];
        if (causaliWF) {
            causaliWF.forEach((causaleWF) => {
                const giornoDt = DateTime.fromISO(causaleWF.giornoDt!).startOf('day').toMillis();
                const dtGiornata = DateTime.fromISO(day.dtGiornata!).startOf('day').toMillis();
                if (!causaleWF.elaborato && (giornoDt === dtGiornata)) {
                    newCausali.push(causaleWF);
                }
            })
        }
        return newCausali;
    };

    static formatDate = (dt: FPNetDate) => {
        if (!dt) return '';
        return DateTime.fromISO(dt).toFormat('dd/LL/yyyy');
    }

    static statoPeriodo = (azienda: AziendaMin, periodo: Periodo, days: PresenzeGiornataPlusRecord[] | null, week: StatoWeekPLUSRecord | null, user: UserCRUDRecord | null) => {
        const livelloPresenzeChiusura = user ? user.presenze.chiusura : 0; //ATTENZIONE: per Employee viene valorizzato l'utente, mentre in FPNet2 no...
        const stato = {
            settimanaNonSalvata: false,
            meseNonSalvato: false,
            periodoNonSalvato: false,
            fuoriSede: false,

            giorni: 0,
            giorniChiusi: 0,
            numGiorni: 0,
            chiuso: false,
            quantitaValida: true,

            settimanaInAnomalia: false,
            periodoInAnomalia: false
        }

        if (days) {
            days.forEach((day) => {
                if (day.mese === periodo.mese) {
                    ++stato.numGiorni

                    if (day.stato > livelloPresenzeChiusura || (day.week && day.week.statoWeek > livelloPresenzeChiusura)) {
                        ++stato.giorniChiusi
                    }
                }

                if (day.totali !== null) {
                    stato.periodoInAnomalia = stato.periodoInAnomalia || PresenzeGiornataPlusRecord.hasAnomalia(day);
                    if (day.modified) {
                        if (day.week === week) {
                            stato.settimanaNonSalvata = true;
                        }
                        if (DateTime.fromISO(day.dtGiornata!).month === periodo.mese) {
                            stato.meseNonSalvato = true;
                        }
                        stato.periodoNonSalvato = true;

                        stato.quantitaValida = stato.quantitaValida && PresenzeGiornataPlusRecord.isQuantitaValide(day);

                    }
                    if (day.week === week) {
                        // Aggiorno fuoriSede per tutti i giorni della settima, N.B. solo l'ultimo giorno è significativo,
                        // lo faccio per tutti i giorni della settimana x valutare anche il caso di cessazione durante la settimana.
                        stato.fuoriSede = GestionePlus.isFuoriSede(day, azienda);
                        stato.settimanaInAnomalia = stato.settimanaInAnomalia || (PresenzeGiornataPlusRecord.hasAnomalia(day) && day.anomalia !== 21);
                    }
                }
            });
            stato.chiuso = (stato.numGiorni === stato.giorniChiusi);
            stato.periodoInAnomalia = stato.periodoInAnomalia || !stato.quantitaValida;
        }

        return stato;
    };

    static isFuoriSede = (day: PresenzeGiornataPlusRecord, {idAzienda, idAziendaMaster}: AziendaMin) => {
        if (day && idAzienda !== idAziendaMaster) {
            return day.idAzienda !== idAzienda;
        } else {
            return false;
        }
    };

    static getCalendario = (cache: CachePresenze, isPaginaWF: boolean, anno: number, prop: LavoratorePresenzeProprietaULRecord) => {
        if (isPaginaWF)
            return cache.getCalendario(prop.idCalendarioWF, anno);
        else
            return cache.getCalendario(prop.idCalendario, anno);
    }

    static getDescrizioneUL = (elencoUnitaLocali: AziendaRecord[], idAzienda: number) => {
        const azienda = elencoUnitaLocali.find((az) => az.idAzienda === idAzienda)
        if (azienda) {
            return azienda.unitaLocale;
        } else {
            return '???';
        }
    }

    static setEditEffettive = (day: PresenzeGiornataPlusRecord) => {
        day.effettive = [];
        const results = PresenzeGiornataPlusRecord.getStrisciataOriginale(day);
        const strisciata = results.strisciata;
        const terminali = results.terminali;
        const strisciataManuale = day.effettiveManualiFlat; // N.B. gli elementi sono stringhe!
        let indNE = 0;
        for (let indE = 0; indE < strisciata.length; ++indE) {
            //var timb = strisciata[indE] === 0 ? null : strisciata[indE];
            // Modifica del 30/10/2019
            let timb = strisciata[indE];
            if (timb === null && (indE < strisciataManuale.length)) { // verifico se la timbratura mancante è stata integrata manualmente
                timb = (!strisciataManuale[indE] || strisciataManuale[indE] === 'null') ? null : parseInt(strisciataManuale[indE]!);
            }
            if (indE % 2 === 0) { // Se pari ==> entrata (partendo da zero che è pari...)
                if (day.effettive.length <= indE) {
                    day.effettive.push(CoppiaTimbrature.factory());
                }
                CoppiaTimbrature.setEntrata(day.effettive[indNE], timb);
                day.effettive[indNE].entrataTerminale = terminali[indE];
            } else { // Se dispari ==> uscita
                CoppiaTimbrature.setUscita(day.effettive[indNE], timb);
                day.effettive[indNE].uscitaTerminale = terminali[indE];
                indNE++;
            }
        }
    }

    static adeguaEffManuali = (day: PresenzeGiornataPlusRecord, effettiveManualiFlat: (string | null)[]) => {
        // normalizzo array per effettive manuali, nel caso vengano editate...
        if (effettiveManualiFlat === null) {
            effettiveManualiFlat = [];
        }
        if (effettiveManualiFlat.length < day.effettiveOriginali.length * 2) {
            for (let ind0 = effettiveManualiFlat.length; ind0 < day.effettiveOriginali.length * 2; ++ind0) {
                effettiveManualiFlat.push(null);
            }
        }
        return effettiveManualiFlat;
    }

    static allineaPrevisto = (day: PresenzeGiornataPlusRecord,
                              turno: TurnoRecord,
                              sviluppaPausa: boolean,
                              elencoTurni: TurnoRecord[],
                              distribuzione?: TurnoDistribuzioneRecord) => {
        day.codTurno = turno.codTurno;
        if (day.codTurno > 0) { // escludo gestione a consuntivo
            distribuzione = distribuzione || TurnoRecord.getDistribuzione(turno, day.idGiornoSviluppo, elencoTurni.length > 0 ? TurnoRecord.getDistribuzione(elencoTurni[0], day.idGiornoSviluppo) : undefined);
            day.idGiorno = day.idGiornoSviluppo;
            PresenzeGiornataPlusRecord.ripristinaPrevisto(day, distribuzione);

            if (sviluppaPausa) {
                day.previste = Timbrature.impostaPausa(day.previste, distribuzione);
            }
        }
        PresenzeGiornataPlusRecord.resetAnomalia(day);
    };

    //static getGiustificativo = (codGiu: string) => GestionePlus.cache.getGiustificativo(codGiu);

    //static getCache = () => GestionePlus.cache;

    private static getDescrizioneAnomaliaByNumber = function (anomalia: number) {
        let text = null;
        switch (anomalia) {
            case -3:
            case -2:
                text = "E' necessario calcolare lo scorporo quantità/anomalie!";
                break;
            case -1:
                text = "Le entrate e le uscite non sono in sequenza corretta";
                break;
            case 0:
                text = "La giornata non presenta anomalie";
                break;
            case 1:
                text = "Le quantità mancanti sono maggiori delle quantità giustificate";
                break;
            case 2:
                text = "Le quantità eccedenti diurne sono maggiori delle quantità giustificate";
                break;
            case 3:
                text = "Le quantità eccedenti notturne sono maggiori delle quantità giustificate";
                break;
            case 4:
                text = "Le quantità giustificate sono maggiori delle quantità da giustificare";
                break;
            case 5:
                text = "Tabelle quantificazione e/o qualificazione eccedenti NON corrette";
                break;
            case 6: // new FPNet2020
                text = "Automatismo pausa flex non applicabile, quadrare manualmente la giornata";
                break;
            case 10: // new FPNet2020
                text = "Profilo presenze non impostato!";
                break;
            case 11: // new FPNet2020
                text = "Raggiunto il limite massimo di causali o causale scartata!";
                break;
            case 12: // new FPNet2020
                text = "Timbrature arrotondate non valide";
                break;
            case 20: // new FPNet2020
                text = "Quadratura di calcolo fallita";
                break;
            case 22: // new FPNet2020
                text = "Quadratura specifica ore prodotte fallita";
                break;
            case 23: // new FPNet2020
                text = "Indicare specifica ore prodotte";
                break;
            case 24: // new FPNet2020
                text = "E' necessario modificare il giustificativo da WF";
                break;
            case 21: // new FPNet2020
                text = PresenzeGiornataPlusRecord.getDescrizioneWarning(2048);
                break;
            case 99: // new FPNet2021
                text = "Quantità non valida di uno o più giustificativi!";
                break;
        }
        return text;
    };

    setPeriodo = (anno: number, mese: IndiceMese) => {
        this.anno = anno;
        this.mese = mese;
    }

    setModified = (day: PresenzeGiornataPlusRecord) => {
        day.modified = true;
        this.modified = true;
    };

    cercaTerminale = (codTerminale: string | null) => {
        const terminali = this.cache.terminali;
        for (let ind0 = 0; ind0 < terminali.length; ++ind0) {
            if (terminali[ind0].codTerminale === codTerminale) {
                return terminali[ind0];
            }
        }
        return null;
    }

    updateTotaliWeekEAccumuli = (week: StatoWeekPLUSRecord): void => {
        const updatedWeek = GestionePlusData.updateTotaliWeek(this, this.lavoratore!, week, this.cache);
        Accumuli.aggiornaSaldiAccumuli(this, updatedWeek);
    }

    updateTotaliMese = () => {
        if (this.totaliMese !== null) {
            this._updateTotaliMese(this.totaliMese, this.soloMeseRif);
            Accumuli.aggiornaSaldiAccumuli(this, null);
        }
    }

    getMinMaxHContr = (week: StatoWeekPLUSRecord) => {
        return GestionePlusData.getMinMaxHContr(this, this.lavoratore!, week);
    }

    allineaIstituti = () => {
        const fineMese = this.getFineMese();
        if (this.ic && this.ic.tabellaIC) {
            const totaliMese = this._updateTotaliMese(StatoWeekPLUSRecord.factory({totali: RiepilogoOrePlus.factory()}), true);
            for (let ind0 = 0; ind0 < this.ic.tabellaIC.length; ++ind0) {
                const istituto = this.ic.tabellaIC[ind0];
                if (istituto && istituto.saldo) {
                    istituto.saldo.maturatiPeriodo = 0;
                    istituto.saldo.godutiPeriodo = 0;
                    if (istituto.saldo.dtGodutiAl && DateTime.fromISO(istituto.saldo.dtGodutiAl).endOf('day') < fineMese) {
                        for (let ind1 = 0; ind1 < istituto.statusGiustificativi.length; ++ind1) {
                            const giustificativo = istituto.statusGiustificativi[ind1];
                            //
                            //
                            const totale = StatoWeekPLUSRecord.getRiepilogoGiu(totaliMese, giustificativo.codiceGiustificativo);
                            if (totale !== null) {
                                if (giustificativo.operazione === 1) { // carica
                                    istituto.saldo.maturatiPeriodo += totale.quantita;
                                } else if (giustificativo.operazione === 2) { // scarica
                                    let divisore = 1;
                                    if (istituto.saldo.te === 1) {
                                        divisore = 8;
                                    }
                                    if (istituto.saldo.te === 2) {
                                        divisore = 6.67;
                                    }
                                    istituto.saldo.godutiPeriodo += (totale.quantita / divisore);
                                } else {
                                    //???
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    setCache = (cache: CachePresenze) => {
        this.cache = cache
    };

    getCache = () => this.cache;

    getCalendario = (prop: LavoratorePresenzeProprietaULRecord) => {
        return GestionePlus.getCalendario(this.getCache(), this.isPaginaWF, this.anno, prop);
    }

    getListaTurni = () => {
        return this.cache.turni;
    }

    getListaGiustificativi = () => {
        return this.cache.elencoGiustificativi;
    }

    hasWF = () => {
        return this.cache.causaliWF.length > 0;
    }

    getPeriodo = (): Periodo => {
        return {anno: this.anno, mese: this.mese};
    }

    saveGestioneInLavoratore = (lavoratore: LavoratorePresenzeRecord | null | undefined) => {
        if (lavoratore) {
            lavoratore.loaded = true;
            lavoratore.anno = this.anno;
            lavoratore.mese = this.mese;
            lavoratore.ic = cloneDeep(this.ic);
            // "Distribuisco" le voci di pianificazione del periodo su ogni singola giornata di competenza
            if (this.days && this.vociPianificazione && this.vociPianificazione.length) {
                for (const giornata of this.days) {
                    giornata.vociPianificazione = cloneDeep(this.vociPianificazione.filter(v => v.dtGiornata === giornata.dtGiornata));
                }
            }
            lavoratore.days = cloneDeep(this.days);
            lavoratore.weeks = cloneDeep(this.weeks);
            lavoratore.totaliMese = cloneDeep(this.totaliMese);

            lavoratore.causaliWF = cloneDeep(this.causaliWF);
            lavoratore.causaliWFElaborate = cloneDeep(this.causaliWFElaborate);

            lavoratore.giustificativiAttestati = cloneDeep(this.giustificativiAttestati);
            lavoratore.giustificativiAttestatiElaborati = cloneDeep(this.giustificativiAttestatiElaborati);

            //lavoratore.vociPianificazione = cloneDeep(this.vociPianificazione);

            const lavoratoreInGestione = this.elencoLavoratori!.find(lav => lav.id === lavoratore.id);
            //const lavoratoreInGestione=this.lavoratore;
            if (lavoratoreInGestione) {
                //console.warn('fisso proprietà!',lavoratoreInGestione.proprieta)
                lavoratore.proprieta = cloneDeep(lavoratoreInGestione.proprieta);
                lavoratore.orarioProprieta = cloneDeep(lavoratoreInGestione.orarioProprieta);
                lavoratore.status = cloneDeep(lavoratoreInGestione.status);
            }
        }
        return {lavoratore, cache: this.getCache()};
    }

    getDescrizioneUL = (idAzienda: number) => {
        return GestionePlus.getDescrizioneUL(this.elencoUnitaLocali, idAzienda);
    }

    /**
     * Wrapper di GestionePlusData.consolidaGiornata, utile all'utilizzo diretto dell'istanza gestione come container di calcolo
     * @param days
     * @param ignoraStatus
     * @param consolidaEffManuali
     */
    public consolidaGiornataInGestione = (day: PresenzeGiornataPlusRecord,
                                          ignoraStatus: boolean = true,
                                          consolidaEffManuali: boolean = true): { consolidatedData: ConsolidatedData, calcolabile: boolean } => {
        const {consolidatedData, calcolabile} = GestionePlusData.consolidaGiornata(this.getPeriodo(), this.lavoratore!, day, this.getCache(), this.isPaginaWF, ignoraStatus);

        if (consolidaEffManuali)
            day.effettiveManualiFlat = GestionePlus.adeguaEffManuali(day, day.effettiveManualiFlat);

        //merge(this, consolidatedData);
        this.proprietaOrario = consolidatedData?.proprietaOrario || null;

        this.elencoStatus = consolidatedData?.elencoStatus || [];
        this.proprietaGestione = consolidatedData?.proprietaGestione || null;
        this.calendarioChiusure = consolidatedData?.calendarioChiusure || null;
        this.elencoTurni = consolidatedData?.elencoTurni || [];
        this.turnoAConsuntivo = consolidatedData?.turnoAConsuntivo || null;
        this.tabFlex = consolidatedData?.tabFlex || null;
        this.tabQualificaEcc = consolidatedData?.tabQualificaEcc || null;
        this.tabQuantificaEcc = consolidatedData?.tabQuantificaEcc || null;
        this.distribuzioneTurno = consolidatedData?.distribuzioneTurno || null;

        return {consolidatedData, calcolabile};
    };

    public setEditEffettive = (inizio: boolean, day: PresenzeGiornataPlusRecord, mask: number) => {
        this.editEffettive = !this.editEffettive;
        if (!inizio) {
            day.maskEffettive = mask; // Aggiorno subito mask, nel caso di annullamento dell'operazione day.maskEffettive nn è stato ancora aggiornato da Angular!
            GestionePlus.setEditEffettive(day);
        }
    };

    public allineaPrevisto = (day: PresenzeGiornataPlusRecord,
                              turno: TurnoRecord,
                              sviluppaPausa: boolean,
                              distribuzione?: TurnoDistribuzioneRecord) => {
        GestionePlus.allineaPrevisto(day, turno, sviluppaPausa, this.elencoTurni, distribuzione);
    }

    public calcolaGiornata = (day: PresenzeGiornataPlusRecord,
                              calcolaEffettive: boolean = false,
                              compensa: boolean = false,
                              calcoloMassivo: boolean = false,
                              chiusuraAnticipata: boolean = false,
                              impostaPrevistoConESenza: boolean = false,
                              normalizzaSTD: boolean = false,
                              giornataBloccataConWF: boolean = false,
    ): void => {
        const calcolo = Calcolo.getInstance(this.getCache(), this.lavoratore!, this as Periodo, this.soloMeseRif, false, this);
        calcolo.calcolaGiornata({
            day2calculate: day,
            calcolaEffettive,
            compensa,
            calcoloMassivo,
            chiusuraAnticipata,
            impostaPrevistoConESenza,
            normalizzaSTD,
            giornataBloccataConWF,
        });
        if (day.modified) {
            this.setModified(day);
        }
        this.allineaIstituti();
    }

    public annullaModifiche = (_day: PresenzeGiornataPlusRecord,): void => {
        // todo
    }

    public avviaGM = (user: UserCRUDRecord): void => {
        // todo finire

        /*if (this.currentWeek !== null) {
            if (this.currentWeek.currentDay) {
                this.chiudi(this.currentWeek.currentDay);
            }
            this.currentWeek = null;
        }

        this.dayIniziale = null; //PresenzeGiornataPlus
        this.dayFinale = null; //PresenzeGiornataPlus
        this.resetSelection();*/

        this.giustificazioneDiMassa = GiustificazioneDiMassaTask.factory({anno: this.anno, mese: this.mese, controllo: this.isPaginaPlus ? 'COME_MANCANTE' : 'COME_PREVISTO'});
        this.giustificazioneDiMassa.livelloPresenzeChiusura = user ? user.presenze.chiusura : 0;
        this.giustificazioneDiMassa.tipoOperazione = "INSERIMENTO";
        this.giustificazioneDiMassa.elaboraGiorniBloccati = !this.isPaginaPlus;
        this.giustificazioneDiMassa.calcolaPresenze = true;
        this.giustificazioneDiMassa.salvaPresenze = false;
        //this.statoGM = 1;
    };

    public terminaGM = (_chiudiDay: boolean): void => {
        // todo
        /*
        this.dayIniziale = null; //PresenzeGiornataPlus
        this.dayFinale = null; //PresenzeGiornataPlus
        this.resetSelection();

        this.giustificazioneDiMassa = null;
        this.statoGM = 0;

        if (chiudiDay) {
            if (this.currentWeek !== null) {
                if (this.currentWeek.currentDay) {
                    this.chiudi(this.currentWeek.currentDay);
                }
                this.currentWeek = null;
            }
        }*/
    }

    public getInizioMese(): DateTime {
        return DateTime.fromObject({year: this.anno, month: this.mese}).startOf('month').startOf('day');
    }

    public getFineMese(): DateTime {
        return DateTime.fromObject({year: this.anno, month: this.mese}).endOf('month').startOf('day');
    }

    hasWorkflow4day = (day: PresenzeGiornataPlusRecord) => {
        return false;
    };

    public consolidaModifiche = (day: PresenzeGiornataPlusRecord,): void => {
        if (this.causaliWFElaborate.length > 0) {
            this.causaliWFElaborate.forEach(key => {
                const causaleWF = this.causaliWF.find(causale => PresenzeCausaleWFRecord.getKey(causale) === key)
                if (causaleWF){
                    causaleWF.elaborato = true;
                    if (causaleWF.tipoCausale === 'MANUTENZIONE_TIMBRATURE') {
                        day.idWF = causaleWF.wf.idWF;
                    }
                }
            });
            this.causaliWFElaborate = [];
        }
        if (this.giustificativiAttestatiElaborati.length > 0) {
            this.giustificativiAttestatiElaborati.forEach(key => {
                const attestato = this.giustificativiAttestati.find(causale => GiustificativoAttestatoINPSRecord.getKey(causale) === key)
                if (attestato) attestato.elaborato = true;
            });
            this.giustificativiAttestatiElaborati = [];
        }
    }

    // SOLO PER ESEMPIO!
    public calcolaInteroPeriodo = (task?: CalcoloPresenzeTask): void => {
        //const calcolo = Calcolo.getInstance(this.getCache(), this.lavoratore!, this, this.soloMeseRif, false, this);
        const calcolo = Calcolo.getInstanceFromGestione(this, this.cache, this.lavoratore!);
        calcolo.calcolaInteroPeriodo(
            (day, impostaPrevistoConESenza, giornataBloccataConWF) => {

                if (this.consolidaGiornataInGestione(day).calcolabile) {
                    const oldDay = null;//JSON.stringify(day, PresenzeGiornataPlusRecord.replacer);
                    calcolo.calcolaGiornata(
                        {
                            day2calculate: day,
                            calcolaEffettive: day.arrotondateLocked, // calcolaEffettive
                            compensa: false, // compensa
                            calcoloMassivo: true, // calcoloMassivo
                            impostaPrevistoConESenza,
                            giornataBloccataConWF
                        });
                    const newDay = JSON.stringify(day, PresenzeGiornataPlusRecord.replacer);
                    //if (day.modified) {
                    if (oldDay === newDay) {
                        day.modified = false;
                    } else {
                        this.setModified(day);
                    }


                    const obj = JSON.parse(newDay);
                    // sort => https://www.npmjs.com/package/json-keys-sort
                    const newDayOrdered = JSON.stringify(sort(obj));
                    day.note = newDayOrdered;


                    this.allineaIstituti();
                    this.consolidaModifiche(day);
                }
            }, task);
    }

    /**
     * Aggiorna i totali mensili
     */
    private _updateTotaliMese = (totaliMese: StatoWeekPLUSRecord, soloMeseRif: boolean) => {
        return Calcolo.updateTotaliMese(this, totaliMese, soloMeseRif, this.cache);
    };

}